// Форматы 3D-моделей, которые принимает просмотрщик. Здесь только разбор без three.js, поэтому он проверяется
// обычными тестами. Сборка геометрии для three.js — в modelGeometry.js.

export const MODEL_FORMATS = ['stl', 'obj', 'ply', 'vtk'];
export const MODEL_ACCEPT = '.stl,.obj,.ply,.vtk,.zip';
export const MAX_MODEL_BYTES = 300 * 1024 * 1024;

/** Формат по имени файла: stl | obj | ply | vtk | zip, иначе null. */
export const formatOf = (name) => {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || ''));
  const ext = m ? m[1].toLowerCase() : '';
  return MODEL_FORMATS.includes(ext) || ext === 'zip' ? ext : null;
};

/** Имя без каталога и расширения: «jaws/upper.stl» -> «upper». */
export const baseName = (name) => String(name || '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '');

/**
 * OBJ: вершины `v` и грани `f` (индексы вида 7, 7/1, 7//2, 7/1/2, в том числе отрицательные).
 * Многоугольники разбиваются веером на треугольники. Остальные строки (нормали, текстуры, материалы) пропускаются.
 */
export const parseObj = (text) => {
  const verts = [];
  const tris = [];
  const lines = String(text).split(/\r?\n/);
  for (let li = 0; li < lines.length; li += 1) {
    const line = lines[li];
    const c = line.charCodeAt(0);
    if (c === 118 && line.charCodeAt(1) === 32) {            // "v "
      const p = line.trim().split(/\s+/);
      verts.push(parseFloat(p[1]), parseFloat(p[2]), parseFloat(p[3]));
    } else if (c === 102 && line.charCodeAt(1) === 32) {     // "f "
      const count = verts.length / 3;
      const ids = line.trim().split(/\s+/).slice(1).map((tok) => {
        const n = parseInt(tok.split('/')[0], 10);
        return n < 0 ? count + n : n - 1;
      });
      for (let k = 1; k + 1 < ids.length; k += 1) tris.push(ids[0], ids[k], ids[k + 1]);
    }
  }
  const count = verts.length / 3;
  if (count === 0 || tris.length === 0) throw new Error('В OBJ нет вершин или граней');
  const indices = new Uint32Array(tris.length);
  for (let i = 0; i < tris.length; i += 1) {
    if (!(tris[i] >= 0 && tris[i] < count)) throw new Error('В OBJ есть грань с несуществующей вершиной');
    indices[i] = tris[i];
  }
  return { positions: Float32Array.from(verts), indices };
};

// Читает count чисел из текста, начиная с pos. Возвращает позицию после последнего числа.
const scanNumbers = (text, pos, count, out) => {
  let i = pos;
  for (let n = 0; n < count; n += 1) {
    while (i < text.length && text.charCodeAt(i) <= 32) i += 1;
    let j = i;
    while (j < text.length && text.charCodeAt(j) > 32) j += 1;
    if (j === i) throw new Error('Неожиданный конец файла VTK');
    out[n] = parseFloat(text.slice(i, j));
    i = j;
  }
  return i;
};

/** VTK legacy ASCII POLYDATA (так сохраняют, например, ParaView и 3D Slicer): POINTS и POLYGONS. */
export const parseVtkAscii = (text) => {
  if (!/^#\s*vtk DataFile/i.test(text)) throw new Error('Это не файл VTK');
  if (!/DATASET\s+POLYDATA/i.test(text)) throw new Error('Поддерживается только VTK POLYDATA');
  const pts = /POINTS\s+(\d+)\s+\w+/i.exec(text);
  const pol = /POLYGONS\s+(\d+)\s+(\d+)/i.exec(text);
  if (!pts || !pol) throw new Error('В VTK не найдены POINTS и POLYGONS');
  const nPoints = parseInt(pts[1], 10);
  const positions = new Float32Array(nPoints * 3);
  scanNumbers(text, pts.index + pts[0].length, nPoints * 3, positions);
  const size = parseInt(pol[2], 10);
  const raw = new Int32Array(size);
  scanNumbers(text, pol.index + pol[0].length, size, raw);
  const tris = [];
  for (let i = 0; i < size;) {
    const k = raw[i];
    for (let j = 1; j + 1 < k; j += 1) tris.push(raw[i + 1], raw[i + 1 + j], raw[i + 2 + j]);
    i += k + 1;
  }
  if (tris.length === 0) throw new Error('В VTK нет граней');
  for (let i = 0; i < tris.length; i += 1) {
    if (!(tris[i] >= 0 && tris[i] < nPoints)) throw new Error('В VTK есть грань с несуществующей вершиной');
  }
  return { positions, indices: Uint32Array.from(tris) };
};

/** true, если это VTK в текстовом виде (во второй-третьей строке стоит ASCII). */
export const isVtkAscii = (buffer) => {
  const head = new TextDecoder('ascii').decode(new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 400)));
  return /^\s*#\s*vtk DataFile[^\n]*\n[^\n]*\n\s*ASCII/i.test(head);
};
